import { Injectable } from '@angular/core';
import { Resource, Service } from 'ngx-jsonapi';
import { lastValueFrom } from 'rxjs';

import { normalizeGeoCoordinate } from '../../utils/geo.utils';

export interface ManagedPerformanceArea {
  id: string;
  label: string;
  latitude: number;
  longitude: number;
  radiusKm: number;
  isPrimary: boolean;
}

export interface PerformanceAreaManagementState {
  areas: ManagedPerformanceArea[];
}

export interface SavePerformanceAreaPayload {
  id?: string;
  label: string;
  latitude: number;
  longitude: number;
  radiusKm: number;
  isPrimary: boolean;
}

export class PerformanceArea extends Resource {
  public override attributes = {
    label: '',
    latitude: 0,
    longitude: 0,
    radius_km: 0,
    is_primary: false,
    created_at: '',
    updated_at: '',
  };

  public override relationships = {};
}

@Injectable({
  providedIn: 'root',
})
export class ProfilePerformanceAreaService extends Service<PerformanceArea> {
  public override resource = PerformanceArea;
  public override type = 'PerformanceArea';
  public override path = '/api/performance-areas';

  public async fetchState(): Promise<PerformanceAreaManagementState> {
    const page = await lastValueFrom(this.all({
      page: {
        number: 1,
        size: 200,
      },
    }));

    return {
      areas: page.data.map((area) => this.toManagedArea(area)),
    };
  }

  public async saveArea(payload: SavePerformanceAreaPayload): Promise<PerformanceAreaManagementState> {
    const area = payload.id
      ? await lastValueFrom(this.get(payload.id))
      : this.new();

    area.attributes.label = payload.label;
    area.attributes.latitude = normalizeGeoCoordinate(payload.latitude);
    area.attributes.longitude = normalizeGeoCoordinate(payload.longitude);
    area.attributes.radius_km = payload.radiusKm;
    area.attributes.is_primary = payload.isPrimary;

    await lastValueFrom(area.save());
    return this.fetchState();
  }

  public async deleteArea(areaId: string): Promise<PerformanceAreaManagementState> {
    await lastValueFrom(this.delete(areaId));
    return this.fetchState();
  }

  private toManagedArea(area: PerformanceArea): ManagedPerformanceArea {
    return {
      id: area.id,
      label: area.attributes.label,
      latitude: Number(area.attributes.latitude),
      longitude: Number(area.attributes.longitude),
      radiusKm: Number(area.attributes.radius_km),
      isPrimary: area.attributes.is_primary,
    };
  }
}
